import { useState } from "react";
import {
  processOrderBookLevels,
  calculateAggregatedData,
  calculateBarWidth,
  shouldHighlightRow,
  getPopoverSide,
} from "@/components/features/trade/OrderBook/utils";

export const useOrderBookSide = (
  levels: [number, number][],
  reverse: boolean,
  side: "bids" | "asks",
  layout: "default" | "tablet" | "mobile",
) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);

  // Process the levels for display
  const processedLevels = processOrderBookLevels(levels);

  // Apply reverse if needed (only for display)
  const finalLevels = reverse ? [...processedLevels].reverse() : processedLevels;

  // Calculate max Quote value for bar width calculation - based on current visible levels only
  const maxQuoteValue = Math.max(...finalLevels.map((level) => level.quoteValue), 1);

  const handleRowHover = (index: number) => {
    if (layout !== "mobile") {
      setHoveredIndex(index);
    }
  };

  const handleRowLeave = () => {
    if (layout !== "mobile") {
      setHoveredIndex(null);
    }
  };

  const handleRowClick = (index: number) => {
    if (layout === "mobile") {
      // If clicking the same row, toggle it off
      if (clickedIndex === index) {
        setClickedIndex(null);
      } else {
        // Otherwise, set new clicked row (this will close any other open popover)
        setClickedIndex(index);
      }
    }
  };

  const handlePopoverClose = (index: number) => {
    if (layout === "mobile" && clickedIndex === index) {
      setClickedIndex(null);
    }
  };

  const getRowData = (index: number) => {
    const level = finalLevels[index];
    if (!level) return null;

    const barWidthPercentage = calculateBarWidth(level.quoteValue, maxQuoteValue);
    const aggregatedData = calculateAggregatedData(finalLevels, index, side, reverse);
    const activeIndex = layout === "mobile" ? clickedIndex : hoveredIndex;
    const isHighlighted = shouldHighlightRow(index, activeIndex, side);
    const popoverSide = getPopoverSide(layout);

    return {
      ...level,
      barWidthPercentage,
      aggregatedData,
      isHighlighted,
      popoverSide,
      isPopoverOpen: layout === "mobile" ? clickedIndex === index : false,
    };
  };

  return {
    finalLevels,
    handleRowHover,
    handleRowLeave,
    handleRowClick,
    handlePopoverClose,
    getRowData,
  };
};
